import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Wrench, Bug, ExternalLink } from "lucide-react";
import { useChangelog } from "@/hooks/useChangelog";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Link } from "react-router-dom";

export function WhatsNewPopover() {
  const { hasUnread, isOpen, setIsOpen, markAsRead, entries, recentCount } = useChangelog();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && hasUnread) {
      markAsRead();
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'feature': return <Sparkles className="h-4 w-4 text-blue-500" />;
      case 'improvement': return <Wrench className="h-4 w-4 text-amber-500" />;
      case 'fix': return <Bug className="h-4 w-4 text-green-500" />;
      default: return <Sparkles className="h-4 w-4" />;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'feature': return 'Nouveauté';
      case 'improvement': return 'Amélioration';
      case 'fix': return 'Correction';
      default: return 'Mise à jour';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" title="Nouveautés">
          <Sparkles className="h-5 w-5" />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-blue-500 border-2 border-background animate-pulse" />
          )}
          <span className="sr-only">Voir les nouveautés</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b">
          <h3 className="font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-500" />
            Nouveautés
            <Badge variant="outline" className="text-xs">
              {recentCount}
            </Badge>
          </h3>
          <p className="text-sm text-muted-foreground">
            Les dernières mises à jour de Piktable
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="p-4 border-b last:border-b-0 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  {getCategoryIcon(entry.category)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="secondary" className="text-xs">
                      {getCategoryLabel(entry.category)}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(entry.date), "d MMM", { locale: fr })}
                    </span>
                  </div>
                  <p className="font-medium text-sm">{entry.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {entry.description}
                  </p>
                  {entry.link && (
                    <Link 
                      to={entry.link} 
                      className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                    >
                      Voir <ExternalLink className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
